<template>
  <div>
    <div class="text-neutral-text-strong font-bold text-lg mb-12">
      Default Sharing Settings
    </div>
    <div class="c-sharing-settings-by-populations-tab">
      <PopulationActions
        v-if="populationsWithSharing.length"
        ref="populationActionsRef"
        :event-site="EVENT_SITES.DEFAULT_SHARING_POPULATIONS_PAGE"
        :is-action-bar-disabled="isLoading || !populationsWithSharing.length"
        :show-edit-button="canEdit"
        @edit-clicked="handleEditClicked"
        @search-applied="handleSearchApplied"
        @filters-applied="handleFiltersApplied"
      />

      <BittsLoading
        :is-loading="isLoading"
        :with-transition="false"
        class="flex-1"
      >
        <div class="flex flex-col gap-12 h-full">
          <PopulationTabPopulationsList
            v-show="filteredPopulations.length"
            ref="populationsListRef"
            :populations="filteredPopulations"
            :can-edit="canEdit"
            :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
            @populations-selected="handlePopulationsSelected"
            @row-clicked="handleRowClicked"
            @has-changes-updated="handleHasChangesUpdated"
            @current-values-updated="updatePopulationCurrentValues"
          />
          <div
            v-if="showEmptyState"
            class="flex items-center justify-center flex-1"
          >
            <BittsEmptyState
              :title="emptyStateConfig.title"
              :description="emptyStateConfig.description"
              font-awesome-icon-class="c-sharing-settings-by-populations-tab__empty-state-icon"
              :font-awesome-icon="emptyStateConfig.icon"
              :font-awesome-icon-style="{ height: '52px', width: '52px' }"
              :button="emptyStateConfig.button"
              @button-clicked="emptyStateConfig.onButtonClick"
            />
          </div>
        </div>
      </BittsLoading>

      <BittsModal
        v-if="showUnsavedChangesModal"
        variant="confirm"
        container-class="c-unsaved-changes-modal"
        primary-button-text="Save Changes"
        secondary-button-text="Discard Changes"
        :action-loading="isSaving"
        @action="handleSaveChanges"
        @secondary-action="handleDiscardChanges"
        @closed="closeModal"
      >
        <template #image>
          <FontAwesomeIcon
            :icon="['fak', 'warning']"
            style="height: 48px; width: 48px; color: currentColor"
            class="text-warning-accent mb-16"
          />
        </template>
        <template #header>
          <div>
            <h3
              class="text-center text-neutral-text-strong font-bold text-xl mb-4"
            >
              Unsaved changes
            </h3>
            <div class="text-center text-neutral-text">
              <p>You have unsaved changes.</p>
              <p>What would you like to do?</p>
            </div>
          </div>
        </template>
      </BittsModal>

      <PopulationSharingModal
        v-if="isPopulationSharingModalOpen"
        v-model:selected-population-ids="selectedPopulationIds"
        :is-multi-select="true"
        @close="handleClosePopulationSharingModal"
        @saved="handlePopulationSharingModalSaved"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsLoading, BittsModal } from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import type { RouteLocationNormalized } from 'vue-router';

import PopulationSharingModal from '@/components/partners/DataSharingSettings/PopulationSharingModal/PopulationSharingModal.vue';

import { crossbeamApi } from '@/api';
import { useSharingHubModal } from '@/composables/dataSharingHub/useSharingHubModal';
import { useSharingHubSelection } from '@/composables/dataSharingHub/useSharingHubSelection';
import useSharingSettingsHub, {
  type FilterValues,
  type PopulationWithSharing,
} from '@/composables/dataSharingHub/useSharingSettingsHub';
import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import useDataSharingTracking from '@/composables/useDataSharingTracking';
import {
  useDataSharePresetsStore,
  useDataSharesStore,
  useFlashesStore,
  usePopulationsStore,
} from '@/stores';

import PopulationActions from '../PopulationActions.vue';

import PopulationTabPopulationsList from './PopulationTabPopulationsList.vue';

const router = useRouter();
const populationsStore = usePopulationsStore();
const dataSharesStore = useDataSharesStore();
const { isGreenfieldSharingBlocked } = storeToRefs(dataSharesStore);
const { refreshDataSharesStore } = dataSharesStore;
const dataSharePresetsStore = useDataSharePresetsStore();
const { addSuccessFlash } = useFlashesStore();
const { trackDataSharingChangeFromContext } = useDataSharingTracking();

const {
  populationsWithSharing,
  filteredPopulations,
  applySearch,
  applyFilters,
  clearFilters,
  updatePopulationCurrentValues,
} = useSharingSettingsHub();

const { canEdit } = useDataSharingPermissions();

const {
  modalType,
  showModal: showUnsavedChangesModal,
  openUnsavedChangesModal,
  closeModal,
} = useSharingHubModal();

const { selectedIds: selectedPopulationIds, clearSelection } =
  useSharingHubSelection<PopulationWithSharing>();

const populationsListRef = ref<InstanceType<
  typeof PopulationTabPopulationsList
> | null>(null);

const isLoading = ref(true);
const isSaving = ref(false);

// Population sharing modal state
const isPopulationSharingModalOpen = ref(false);

const showEmptyState = computed(() => {
  return !filteredPopulations.value.length;
});

const emptyStateConfig = computed(() => {
  const hasNoPopulations = !populationsWithSharing.value.length;

  if (hasNoPopulations) {
    return {
      title: 'No populations yet',
      description:
        'Default sharing settings are configured per population. Create a population to define how data is shared with partners by default.',
      icon: ['fak', 'population'] as ['fak', string],
      button: {
        text: 'Populations',
        size: 'small' as const,
        variant: 'outline' as const,
        type: 'primary' as const,
      },
      onButtonClick: handleGoToPopulations,
    };
  }

  return {
    title: 'No results',
    description: 'Try different filters to find your population',
    icon: ['fak', 'search'] as ['fak', string],
    button: {
      text: 'Clear Filters',
      size: 'small' as const,
      variant: 'outline' as const,
      type: 'primary' as const,
    },
    onButtonClick: handleClearFilters,
  };
});

function handleEditClicked() {
  isPopulationSharingModalOpen.value = true;
}

function handleClosePopulationSharingModal() {
  isPopulationSharingModalOpen.value = false;
}

function handlePopulationSharingModalSaved() {
  clearSelection();
}

function handleSearchApplied(value: string) {
  applySearch(value);
}

function handleFiltersApplied(filterValues: FilterValues) {
  applyFilters(filterValues);
}

const populationActionsRef = ref<InstanceType<typeof PopulationActions> | null>(
  null,
);

function handleClearFilters() {
  clearFilters();
  populationActionsRef.value?.clearAllFilters();
}

function handleGoToPopulations() {
  router.push({ name: 'populations' });
}

function handlePopulationsSelected(populationIds: number[]) {
  selectedPopulationIds.value = populationIds;
}

watch(selectedPopulationIds, (ids) => {
  populationsListRef.value?.setSelection(ids);
});

function handleRowClicked(populationId: number) {
  const targetRoute = {
    name: 'sharing-settings-by-population',
    params: { population_id: populationId },
  };

  if (hasAnyChanges.value) {
    openUnsavedChangesModal(targetRoute as unknown as RouteLocationNormalized);
  } else {
    router.push(targetRoute);
  }
}

async function handleSaveChanges() {
  await saveChanges();

  const routeToNavigate = closeModal();
  if (routeToNavigate) {
    await router.push(routeToNavigate);
  }
}

async function handleDiscardChanges() {
  populationsListRef.value?.reset();

  const routeToNavigate = closeModal();
  if (routeToNavigate) {
    await router.push(routeToNavigate);
  }
}

const hasAnyChanges = ref(false);

function handleHasChangesUpdated(hasChangesValue: boolean) {
  hasAnyChanges.value = hasChangesValue;
}

onBeforeMount(async () => {
  await Promise.all([
    populationsStore.readySync,
    dataSharesStore.readySync,
    dataSharePresetsStore.readySync,
  ]);
  isLoading.value = false;
});

onBeforeRouteLeave((to, _from, next) => {
  if (!modalType.value && hasAnyChanges.value) {
    openUnsavedChangesModal(to);
    next(false);
  } else {
    next();
  }
});

async function saveChanges() {
  const changedItems = populationsListRef.value?.getAllChangedItems() ?? [];

  if (changedItems.length === 0) return;

  const dataShares = changedItems
    .filter((item) => item.current.visibility !== undefined)
    .map((item) => {
      const visibility = item.current.visibility as
        | 'hidden'
        | 'aggregates_only'
        | 'full'
        | 'greenfield_full';

      // preset_id should be undefined for hidden and aggregates_only
      const presetId =
        visibility === 'hidden' || visibility === 'aggregates_only'
          ? undefined
          : (item.current.presetId ?? undefined);

      return {
        population_id: item.id,
        visibility,
        preset_id: presetId,
      };
    });

  if (dataShares.length === 0) return;

  const payload = {
    data_shares: dataShares,
  };

  isSaving.value = true;
  const { error } = await crossbeamApi.POST(
    '/v0.1/data-share-assignments/populations',
    {
      body: payload,
    },
  );

  if (error) {
    isSaving.value = false;
    return;
  }

  await refreshDataSharesStore();

  const savedItems = changedItems.filter(
    (item) => item.current.visibility !== undefined,
  );
  savedItems.forEach((item) => {
    const population = populationsStore.getPopulationById(item.id);
    trackDataSharingChangeFromContext({
      population: population ?? undefined,
      current: {
        visibility: item.current.visibility ?? '',
        presetId: item.current.presetId ?? null,
      },
      previous: {
        visibility: item.original.visibility,
        presetId: item.original.presetId ?? null,
      },
      eventSite: EVENT_SITES.DEFAULT_SHARING_POPULATIONS_PAGE,
      bulk: false,
    });
  });

  // Commit the changes as new "original" state after successful save
  populationsListRef.value?.commitChanges();
  addSuccessFlash({ message: 'Settings saved' });
  isSaving.value = false;
}

// Expose to parent component
defineExpose({
  hasAnyChanges,
  isSaving,
  saveChanges,
  hasPopulations: computed(() => populationsWithSharing.value.length > 0),
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.c-sharing-settings-by-populations-tab {
  @apply flex flex-col gap-20 h-full;

  :deep(.c-loading) {
    @apply flex-1;
  }
}

:deep(.c-sharing-settings-by-populations-tab__empty-state-icon) {
  @apply text-neutral-text-weak p-[26px] rounded-[52px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      color-mix(in srgb, theme(colors.neutral-accent) 20%, transparent),
      color-mix(in srgb, theme(colors.neutral-accent) 0%, transparent)
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}

:deep(.c-unsaved-changes-modal .o-bitts-modal__header) {
  @apply !px-24 !py-40 !m-0;
}
</style>
